import { z } from 'zod';
import { applyDocumentMask } from '../../users/utils/masks';
import { isValidCnpj } from '../../users/utils/isValidCnpj';
import { isValidCpf } from '../../users/utils/isValidCpf';

const phoneRegex = /^\(\d{2}\) \d{5}-\d{4}$/;
const zipCodeRegex = /^\d{5}-\d{3}$/;

const validateDocument = (document: string): boolean => {
	const digits = document.replace(/\D/g, '');
	if (digits.length === 11) return isValidCpf(digits);
	if (digits.length === 14) return isValidCnpj(digits);
	return false;
};

export const profileSchema = z.object({
	name: z
		.string()
		.min(2, 'Nome deve ter no mínimo 2 caracteres')
		.regex(/^[a-zA-Z\u00C0-\u00FF'\s-]+$/, 'Nome deve conter apenas letras'),
	surName: z
		.string()
		.min(2, 'Sobrenome deve ter no mínimo 2 caracteres')
		.regex(/^[a-zA-Z\u00C0-\u00FF'\s-]+$/, 'Sobrenome deve conter apenas letras'),
	email: z
		.string()
		.min(1, 'E-mail é obrigatório')
		.refine((val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
			message: 'E-mail inválido',
		}),
	phoneNumber: z
		.string()
		.refine((val) => !val || phoneRegex.test(val), 'Telefone inválido')
		.optional()
		.or(z.literal('')),
	document: z
		.string()
		.refine((val) => !val || [11, 14].includes(val.replace(/\D/g, '').length), 'CPF/CNPJ inválido')
		.refine((val) => !val || validateDocument(val), 'CPF/CNPJ inválido')
		.optional()
		.or(z.literal('')),
	address: z.object({
		zipCode: z
			.string()
			.refine((val) => !val || zipCodeRegex.test(val), 'CEP inválido')
			.optional()
			.or(z.literal('')),
		state: z.string().max(2).optional().or(z.literal('')),
		city: z
			.string()
			.refine((val) => !val || val.length >= 2, 'Cidade deve ter no mínimo 2 caracteres')
			.optional()
			.or(z.literal('')),
		district: z.string().optional().or(z.literal('')),
		street: z
			.string()
			.refine((val) => !val || val.length >= 3, 'Rua deve ter no mínimo 3 caracteres')
			.optional()
			.or(z.literal('')),
		number: z
			.string()
			.optional()
			.or(z.literal('')),
	}),
});

export const profileEditSchema = z.object({
	formData: profileSchema,
	avatarFile: z.instanceof(File).optional(),
	backgroundFile: z.instanceof(File).optional(),
});

export type ProfileEditData = z.infer<typeof profileEditSchema>;

export type ProfileFormData = z.infer<typeof profileSchema>;

export const formatDocument = applyDocumentMask;
export const formatCPF = formatDocument;

export const formatPhone = (value: string): string => {
	if (!value) return '';
	const cleaned = value.replace(/\D/g, '');
	const regex = /^(\d{0,2})(\d{0,5})(\d{0,4})$/;
	const match = regex.exec(cleaned);

	if (match) {
		const parts = [match[1], match[2], match[3]].filter(Boolean);
		if (parts.length === 0) return '';
		if (parts.length === 1) return parts[0];
		if (parts.length === 2) return `(${parts[0]}) ${parts[1]}`;
		return `(${parts[0]}) ${parts[1]}-${parts[2]}`;
	}

	return value;
};

export const formatZipCode = (value: string): string => {
	if (!value) return '';
	const cleaned = value.replace(/\D/g, '');
	const regex = /^(\d{0,5})(\d{0,3})$/;
	const match = regex.exec(cleaned);

	if (match) {
		return [match[1], match[2]].filter(Boolean).join('-');
	}

	return value;
};
